import React, { useEffect, useState } from 'react';

import Select from '@atlaskit/select';
import Tooltip from '@atlaskit/tooltip';

import { Label } from '@qality/common-components';
import { ISSUE_TYPE } from '@qality/common-config';
import { StorageOfUniqueServices } from '@qality/common-service';

import { RequirementSelectContainerStyled } from './RequirementSelect.styled';

const SKIP_REQUIREMENTS: OptionType = {
  label: 'Skip requirements',
  value: '',
};

type Props = {
  onChange: (selected: [{ name: string; value: string }]) => undefined;
  projectId: string;
  requirementTypeName: string;
  isDisabled: boolean;
  tooltip?: string;
};

type OptionType = {
  label: string;
  value: string;
};

const RequirementSelect: React.FC<Props> = ({
  onChange,
  projectId,
  requirementTypeName,
  isDisabled,
  tooltip,
}: Props) => {
  const [options, setOptions] = useState<Array<OptionType>>();
  const [selectedRequirement, setSelectedRequirement] = useState<OptionType>();

  useEffect(() => {
    if (!selectedRequirement) {
      initSelectedRequirement();
    } else {
      updateSelectedRequirement();
    }
  }, [projectId]);

  const initSelectedRequirement = async () => {
    const mappedOptions = await getIssueTypesAsOptions();
    if (!findRequirementTypeByName(mappedOptions, selectedRequirement?.value)) {
      const requirementOptionFromFilters = findRequirementTypeByName(
        mappedOptions,
        requirementTypeName
      );
      const requirementOption =
        requirementOptionFromFilters ?? SKIP_REQUIREMENTS;
      if (!requirementOptionFromFilters) {
        onChange([{ name: 'requirementTypeName', value: '' }]);
      }
      setSelectedRequirement(requirementOption);
    }
    setOptions(mappedOptions);
  };

  const updateSelectedRequirement = async () => {
    const mappedOptions = await getIssueTypesAsOptions();
    if (!findRequirementTypeByName(mappedOptions, selectedRequirement?.value)) {
      const requirementOption = SKIP_REQUIREMENTS;
      onChange([{ name: 'requirementTypeName', value: '' }]);
      setSelectedRequirement(requirementOption);
    }
    setOptions(mappedOptions);
  };

  const getIssueTypesAsOptions = async () => {
    const issueTypes = await StorageOfUniqueServices.get(
      'IssueTypesService'
    ).getIssueTypes(projectId);

    return formatIssueTypesToOptions(issueTypes);
  };

  const formatIssueTypesToOptions: (issueTypes) => Array<OptionType> = (
    issueTypes
  ) => {
    const mappedOptions = issueTypes
      .filter((type) => type.name !== ISSUE_TYPE.QALITY_TEST)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((issueType) => {
        return {
          label: issueType.name,
          value: issueType.name,
        };
      });
    mappedOptions.unshift(SKIP_REQUIREMENTS);
    return mappedOptions;
  };

  const findRequirementTypeByName = (
    mappedOptions: Array<OptionType>,
    name: string
  ) => {
    return mappedOptions.find((mappedOption) => {
      return mappedOption.value === name;
    });
  };

  const handleRequirementChange = async (
    value: OptionType | null
  ): Promise<void> => {
    const selectedOption = value ? value : SKIP_REQUIREMENTS;
    setSelectedRequirement(selectedOption);
    onChange([{ name: 'requirementTypeName', value: selectedOption.value }]);
  };

  return (
    <RequirementSelectContainerStyled>
      <Label text='Requirement' />
      <Tooltip content={tooltip}>
        <Select
          id='qality-requirement-select'
          isDisabled={isDisabled}
          className='requirement-select'
          classNamePrefix='solkit'
          options={options}
          onChange={handleRequirementChange}
          value={selectedRequirement}
        />
      </Tooltip>
    </RequirementSelectContainerStyled>
  );
};

export default RequirementSelect;
